import { useSearchParams } from "react-router";
import { TabGroup, TabList, TabPanels } from "@headlessui/react";
import { Tab } from "@/components/ui/tab";
import { parseISO, isFuture } from "date-fns";
import { Spinner } from "@/components/ui/spinner";
import { Badge } from "@/components/ui/badge";
import { CalendarIcon } from "@heroicons/react/24/outline";

import { AppointmentPanel } from "./appointment-panel";
import { useAppointments } from "../api/get-appointments";

const NoAppointments = () => {
    return (
        <div className="col-span-full mx-4 mx-auto my-8 text-center md:mx-8">
            <CalendarIcon className="mx-auto size-15" />
            <h3 className="mt-2 mb-3 text-xl/8 font-semibold">
                No appointments found
            </h3>
        </div>
    );
};

export const AppointmentList = () => {
    const { data: appointments = [], isLoading } = useAppointments();

    const mappedAppointments = appointments.map((a) => ({
        ...a,
        date: parseISO(a.startDate),
    }));

    const future = [];
    const past = [];

    for (const a of mappedAppointments) {
        if (isFuture(a.date)) future.push(a);
        else past.push(a);
    }

    const sortAsc = (a, b) => a.date - b.date;
    const sortDesc = (a, b) => b.date - a.date;

    const upcomingAppointments = future
        .filter((a) => a.statusID === 2)
        .sort(sortAsc);

    const pendingClinicianApproval = future
        .filter((a) => a.statusID === 0)
        .sort(sortAsc);

    const pendingPatientApproval = future
        .filter((a) => a.statusID === 1)
        .sort(sortAsc);

    const previousAppointments = past
        .filter((a) => a.statusID !== 3 || a.statusID !== 4)
        .sort(sortDesc);

    const declinedAppointments = mappedAppointments
        .filter((a) => a.statusID === 3 || a.statusID === 4)
        .sort(sortDesc);

    const tabs = [
        {
            id: "upcoming",
            label: "Upcoming",
            count: upcomingAppointments.length,
            variant: "success",
        },
        {
            id: "waiting-your-approval",
            label: "Waiting Your Approval",
            count: pendingClinicianApproval.length,
            variant: pendingClinicianApproval.length == 0 ? "light" : "warning",
        },
        {
            id: "waiting-patient-approval",
            label: "Waiting Patient Approval",
            count: pendingPatientApproval.length,
            variant: pendingPatientApproval.length == 0 ? "light" : "warning",
        },
        {
            id: "declined-cancelled",
            label: "Declined/Cancelled",
            count: declinedAppointments.length,
            variant: "danger",
        },
        {
            id: "previous",
            label: "Previous",
            count: previousAppointments.length,
            variant: "light",
        },
    ];

    const [searchParams, setSearchParams] = useSearchParams();

    const currentTab = searchParams.get("tab");

    // Select tab if in searchparams
    const selectedIndex = Math.max(
        tabs.findIndex((tab) => tab.id === currentTab),
        0,
    );

    if (isLoading) {
        return (
            <div className="relative col-span-full">
                <div className="absolute inset-0 z-10">
                    <div className="grid h-50 place-items-center py-10">
                        <Spinner size="lg" />
                    </div>
                </div>
            </div>
        );
    }

    if (appointments.length === 0) {
        return <NoAppointments />;
    }

    return (
        <TabGroup
            selectedIndex={selectedIndex}
            onChange={(index) => {
                setSearchParams({
                    tab: tabs[index].id,
                });
            }}
            className="relative flex flex-col"
        >
            <TabList className="flex max-w-[calc(100dvw-2rem)] scrollbar-none items-stretch overflow-x-auto pb-2">
                {tabs.map((tab) => (
                    <Tab key={tab.id}>
                        {tab.label}

                        <Badge variant={tab.variant} size="sm">
                            {tab.count}
                        </Badge>
                    </Tab>
                ))}
            </TabList>
            <TabPanels className="container mx-auto mt-4 flex-1">
                <AppointmentPanel
                    appointments={upcomingAppointments}
                    upcoming
                />
                <AppointmentPanel
                    appointments={pendingClinicianApproval}
                    upcoming
                />
                <AppointmentPanel
                    appointments={pendingPatientApproval}
                    upcoming
                />
                <AppointmentPanel appointments={declinedAppointments} />
                <AppointmentPanel appointments={previousAppointments} />
            </TabPanels>
        </TabGroup>
    );
};
